import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Application,
	Feedback,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { useInterviewWizard } from '../useInterviewWizard';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { transformToAnswersForMandatoryQuestionsFromGRPC } from '../../../shared/Utils';

export interface ViewCareTeamFamilyMemberInterviewAnswersContextType {
	application: Partial<Application>;
	dietaryHabitsAndHealth: Feedback;
	setDietaryHabitsAndHealth: (feedback: Feedback) => void;
	onSubmitClick: () => void;
	onCancelClick: () => void;
}

interface Props {
	children: ReactNode;
}

const ViewCareTeamFamilyMemberInterviewAnswersContext =
	createContext<ViewCareTeamFamilyMemberInterviewAnswersContextType>({
		application: {},
		dietaryHabitsAndHealth: {},
		setDietaryHabitsAndHealth: () => {},
		onSubmitClick: () => {},
		onCancelClick: () => {},
	});

export const ViewCareTeamFamilyMemberInterviewAnswersProvider: FC<Props> = ({
	children,
}: Props) => {
	const { showToastMessage } = useToastMessage();
	const [application, setApplication] = useState<Partial<Application>>({});
	const [dietaryHabitsAndHealth, setDietaryHabitsAndHealth] =
		useState<Feedback>({});
	const {
		applicationId,
		navigateWithApplicationId,
		fetchApplication: callFetchApplication,
	} = useApplicationId();
	const { onCancelClick } = useInterviewWizard();

	const onSubmitClick = useCallback(() => {
		navigateWithApplicationId(INTERVIEW_LANDING);
	}, [navigateWithApplicationId]);

	const value =
		useMemo<ViewCareTeamFamilyMemberInterviewAnswersContextType>(() => {
			return {
				application,
				dietaryHabitsAndHealth,
				setDietaryHabitsAndHealth,
				onSubmitClick,
				onCancelClick,
			};
		}, [
			application,
			dietaryHabitsAndHealth,
			setDietaryHabitsAndHealth,
			onSubmitClick,
			onCancelClick,
		]);

	const fetchApplication = useCallback(async () => {
		if (!applicationId) {
			showToastMessage('error', 'applicationId is missing');
		}

		try {
			const fetchedApplication = await callFetchApplication();

			const familyMemberInterviewFeedbackWithAnswersForMandatoryQuestions =
				transformToAnswersForMandatoryQuestionsFromGRPC(
					fetchedApplication.familyMemberInterviewFeedback
				);

			if (
				!dietaryHabitsAndHealth ||
				Object.keys(dietaryHabitsAndHealth).length === 0
			) {
				setDietaryHabitsAndHealth(
					familyMemberInterviewFeedbackWithAnswersForMandatoryQuestions?.dietaryHabitsAndHealth ??
						{}
				);
			}
			setApplication(fetchedApplication);
		} catch (e) {
			// Handled in callApi
		}
	}, [
		applicationId,
		callFetchApplication,
		dietaryHabitsAndHealth,
		showToastMessage,
	]);

	useEffect(() => {
		void fetchApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ViewCareTeamFamilyMemberInterviewAnswersContext.Provider value={value}>
			{children}
		</ViewCareTeamFamilyMemberInterviewAnswersContext.Provider>
	);
};

export const useViewCareTeamFamilyMemberInterviewAnswers = () => {
	return useContext(ViewCareTeamFamilyMemberInterviewAnswersContext);
};
